/**
 * RoomClient — framework-free orchestrator for the join → produce → consume
 * lifecycle. UI components interact with it indirectly through the zustand
 * store; this class never imports React.
 *
 * Join sequence (happy path, US1/US2):
 *   1.  Open WebSocket (SignalingClient.connect)
 *   2.  Send `join` → receive routerRtpCapabilities + peers[]
 *   3.  Load mediasoup-client Device with those capabilities
 *   4.  Create send + recv WebRtcTransports on the device, each driven
 *       by their respective server transport via signaling round-trips
 *   5.  Pre-register snapshot peers; subscribe to dynamic notifications
 *   6.  For every existing peer's producer: consume + resumeConsumer,
 *       attaching the resulting track to a per-peer MediaStream in the store.
 *       Store updates drive React tile rendering immediately — the joiner sees
 *       remote media without waiting for the local permission prompt.
 *   7.  getUserMedia → produce audio + video tracks on the send transport
 *       (local permission prompt only gates outgoing media, not inbound)
 *       → setConnectionStatus('connected')
 *
 * Phase 4 (US2) adds: dynamic newPeer/peerLeft/newProducer/producerClosed
 * handlers, permission-denied UX, retryLocalMedia.
 *
 * Bookkeeping for T063/T064/T065:
 *   producerIndex: Map<producerId, { consumerId, peerId, track }>
 *   peerConsumers: Map<peerId, Set<consumerId>>
 * These let us close individual consumers (producerClosed) or all of a peer's
 * consumers (peerLeft) without iterating the full consumers map.
 */
import { Device } from 'mediasoup-client'
import type {
  Consumer,
  Producer,
  RtpCapabilities,
  RtpCodecCapability,
  Transport,
  TransportOptions,
} from 'mediasoup-client/types'
import {
  SignalingClient,
  SignalingError,
  type RequestData,
} from './signaling.js'
import {
  getLocalMedia,
  stopStream,
  videoCaptureConstraints,
  videoMaxBitrate,
  VIDEO_START_BITRATE,
  VIDEO_SCALABILITY_MODE,
} from './media.js'
import { useRoomStore, type PeerInfo } from './store.js'
import { nextFacing, type CameraFacing } from './camera.js'
import { isMobileDevice, chooseVideoSend } from './deviceProfile.js'
import { debugLog } from '../debug/index.js'
import {
  classifyInbound,
  classifyOutbound,
  aggregateLevels,
  type QualityLevel,
  type QualitySample,
} from './connectionQuality.js'

export interface RoomClientOptions {
  wsUrl: string
}

interface JoinResponseData {
  peerId: string
  displayName: string
  routerRtpCapabilities: RtpCapabilities
  peers: Array<{
    peerId: string
    displayName: string
    producers: Array<{ id: string; kind: 'audio' | 'video' }>
    mediaState: { mic: boolean; cam: boolean }
  }>
}

interface CreateTransportResponseData {
  transportId: string
  iceParameters: TransportOptions['iceParameters']
  iceCandidates: TransportOptions['iceCandidates']
  dtlsParameters: TransportOptions['dtlsParameters']
  sctpParameters?: TransportOptions['sctpParameters']
}

interface ConsumeResponseData {
  consumerId: string
  producerId: string
  kind: 'audio' | 'video'
  rtpParameters: unknown
}

interface ProducerEntry {
  consumerId: string
  peerId: string
  track: MediaStreamTrack
}

/** Loosely-typed views over the standard RTCStats reports we read for quality (007). */
interface InboundRtpLike {
  type?: string
  packetsReceived?: number
  packetsLost?: number
  jitter?: number
}
interface RemoteInboundRtpLike {
  type?: string
  fractionLost?: number
  roundTripTime?: number
}

export class RoomClient {
  private readonly signaling: SignalingClient
  private device: Device | null = null
  private sendTransport: Transport | null = null
  private recvTransport: Transport | null = null
  private readonly producers = new Map<string, Producer>()
  private readonly consumers = new Map<string, Consumer>()
  private localStream: MediaStream | null = null
  /** Typed references to local producers for pause/resume (T077). */
  private micProducer: Producer | null = null
  private camProducer: Producer | null = null
  /** Set once we've called join — used to detect double-join. */
  private joined = false
  /** Set once we begin tearing down — leave() is idempotent. */
  private leaving = false
  /** Currently selected camera facing. Mirrors the store; source of truth for next switch. */
  private cameraFacing: CameraFacing = 'user'
  /** Re-entrancy guard: prevents overlapping camera-switch acquisitions (FR-009). */
  private switchInFlight = false
  /** Cleanup callback for the devicechange listener. */
  private deviceChangeCleanup: (() => void) | null = null

  /**
   * Bookkeeping for dynamic producer/consumer lifecycle (T063/T064/T065).
   * producerIndex maps a remote producerId → { consumerId, peerId, track }
   * so we can locate and close the right consumer on producerClosed.
   */
  private readonly producerIndex = new Map<string, ProducerEntry>()
  /**
   * peerConsumers maps peerId → Set of consumerIds so we can tear down
   * every consumer for a peer when peerLeft fires.
   */
  private readonly peerConsumers = new Map<string, Set<string>>()
  /**
   * Canonical per-peer MediaStream that ACCUMULATES tracks over its lifetime.
   * Only RoomClient mutates these; the store always holds a clone derived from
   * the canonical stream via replacePeerStream(peerId, new MediaStream(canonical.getTracks())).
   *
   * This is the fix for the concurrent-clobber bug (US2): because both audio
   * and video tracks are added to the single canonical stream synchronously
   * before any store clone is pushed, neither handler can overwrite the other's
   * track — the last clone always reflects the full accumulated track set.
   */
  private readonly peerStreams = new Map<string, MediaStream>()

  /** Unsubscribe callbacks returned by signaling.on(). Cleared in leave(). */
  private readonly notificationUnsubs: Array<() => void> = []

  /**
   * Connection-quality polling (007). The interval samples getStats() on every
   * remote Consumer + the local Producers, classifies, and writes per-participant
   * QualityLevels to the store. `qualitySamples` holds the previous inbound
   * sample per consumerId (carrying the smoothed EWMA loss ratio) to compute
   * deltas. All ephemeral — cleared in leave().
   */
  private static readonly QUALITY_POLL_MS = 2000
  private qualityTimer: ReturnType<typeof setInterval> | null = null
  private readonly qualitySamples = new Map<string, QualitySample>()

  constructor(opts: RoomClientOptions) {
    this.signaling = new SignalingClient(opts.wsUrl)
  }

  async join(
    roomId: string,
    displayName: string,
    nameSource: 'typed' | 'guest' = 'typed',
  ): Promise<void> {
    if (this.joined) throw new Error('RoomClient.join called twice')
    this.joined = true

    const store = useRoomStore.getState()
    store.setConnectionStatus('connecting')

    this.signaling.onClose(() => {
      if (this.leaving) return
      useRoomStore.getState().setConnectionStatus('lost')
    })

    try {
      await this.signaling.connect()

      const joinResp = (await this.signaling.request('join', {
        roomId,
        displayName,
        nameSource,
        // 010 — tell the server this is a mobile/tablet receiver so it caps the
        // VP9 spatial layer it forwards to our video consumers (a phone can't
        // decode the publisher's full 1080p top layer smoothly). Desktops omit
        // this / send false and keep full per-receiver SVC adaptation (006).
        isMobile: isMobileDevice(),
      } satisfies RequestData<'join'>)) as JoinResponseData

      // 3. Device load
      this.device = new Device()
      await this.device.load({ routerRtpCapabilities: joinResp.routerRtpCapabilities })

      // 4. Send + recv transports
      this.sendTransport = await this.createTransport('send')
      this.recvTransport = await this.createTransport('recv')

      // Local state — adopt the server's authoritative display name.
      // For guest joins, the server may have uniquified the candidate name.
      useRoomStore.getState().setLocalPeerId(joinResp.peerId)
      useRoomStore.getState().setLocalDisplayName(joinResp.displayName)

      // 5. Pre-register every snapshot peer SYNCHRONOUSLY (canonical stream,
      //    store entry, consumer set) before subscribing. This must happen
      //    before subscribeNotifications(): the signaling layer buffers
      //    notifications that arrive before a handler exists and flushes them
      //    the instant we subscribe. A buffered `newProducer` for a snapshot
      //    peer would otherwise flush while that peer's canonical stream did
      //    not yet exist (it used to be created lazily inside the async consume
      //    loop), and handleNewProducer would drop it — losing that peer's
      //    track. Registering up front guarantees the stream is present; the
      //    consume loop and any flushed newProducer then converge on the same
      //    canonical stream, and consumeRemoteProducer's idempotency guard
      //    prevents double-consume.
      for (const remote of joinResp.peers) {
        this.peerStreams.set(remote.peerId, new MediaStream())
        this.peerConsumers.set(remote.peerId, new Set())
        const peerInfo: PeerInfo = {
          peerId: remote.peerId,
          displayName: remote.displayName,
          stream: new MediaStream(),
          mediaState: remote.mediaState,
        }
        useRoomStore.getState().addPeer(peerInfo)
      }

      // 6. Subscribe to dynamic notifications. Snapshot peers are already
      //    registered (step 5), so flushed buffered notifications resolve
      //    against existing canonical streams.
      this.subscribeNotifications()

      // 7. Consume every existing peer's producers BEFORE acquiring local media.
      //    This ensures the joiner sees/hears existing participants immediately,
      //    without waiting for the browser permission prompt (US2 acceptance
      //    scenario #6). consumeRemoteProducer adds each track to the canonical
      //    stream registered in step 5 and pushes a fresh clone to the store —
      //    those store updates drive React tile rendering regardless of
      //    connectionStatus, so remote media is visible as soon as this loop
      //    completes even while acquireAndProduce is still pending below.
      //    If a newProducer for one of these producers already flushed and
      //    consumed it, the idempotency guard skips it here.
      for (const remote of joinResp.peers) {
        for (const producer of remote.producers) {
          await this.consumeRemoteProducer(producer.id, remote.peerId)
        }
      }

      // 8. Local media + produce. acquireAndProduce calls getLocalMedia which
      //    may block on the browser permission prompt. Moving this AFTER the
      //    consume loop means the permission prompt gates only the joiner's OWN
      //    outgoing media — not inbound remote media. getLocalMedia returns a
      //    discriminated result so a denied permission sets localMediaError
      //    without throwing; real failures propagate and are caught below.
      await this.acquireAndProduce()

      useRoomStore.getState().setConnectionStatus('connected')
      debugLog('join.connected', { peerId: joinResp.peerId, roomId })

      // Start per-participant connection-quality polling (007). Runs until leave().
      this.startQualityPolling()
    } catch (err) {
      useRoomStore.getState().setConnectionStatus('lost')
      throw err
    }
  }

  /**
   * Re-request getUserMedia and produce tracks if it now succeeds.
   * Safe to call while still in the room (US2 / T067).
   */
  async retryLocalMedia(): Promise<void> {
    const mediaResult = await getLocalMedia({
      video: videoCaptureConstraints(isMobileDevice()),
      audio: true,
    })
    if (!mediaResult.ok) {
      useRoomStore.getState().setLocalMediaError(mediaResult.reason)
      return
    }

    // Success: clear the error and produce.
    useRoomStore.getState().setLocalMediaError(null)
    await this.produceLocalTracks(mediaResult.stream)

    // produceLocalTracks creates fresh, *resumed* producers and optimistically
    // sets localMediaState. If a stale mute/cam-off was sent to the server
    // before this retry (e.g. the user toggled while permission was denied),
    // the server would still believe this peer is off while it is now actually
    // transmitting — peers would show it muted. Resync server + store +
    // producers to the real post-retry state via the toggle methods.
    this.setMicEnabled(this.micProducer !== null)
    this.setCamEnabled(this.camProducer !== null)

    // Refresh device count after retry — permission may now be granted.
    void this.refreshVideoInputCount()
  }

  /** Idempotent — safe to call from Leave button, useEffect cleanup, etc. */
  leave(): void {
    if (this.leaving) return
    this.leaving = true

    // Stop connection-quality polling and drop its samples (007).
    this.stopQualityPolling()

    // Unsubscribe notification handlers
    for (const unsub of this.notificationUnsubs) unsub()
    this.notificationUnsubs.length = 0

    // Remove the devicechange listener if registered.
    this.deviceChangeCleanup?.()
    this.deviceChangeCleanup = null

    for (const c of this.consumers.values()) c.close()
    this.consumers.clear()
    this.producerIndex.clear()
    this.peerConsumers.clear()
    this.peerStreams.clear()

    for (const p of this.producers.values()) p.close()
    this.producers.clear()
    this.micProducer = null
    this.camProducer = null

    this.sendTransport?.close()
    this.recvTransport?.close()
    this.sendTransport = null
    this.recvTransport = null

    stopStream(this.localStream)
    this.localStream = null

    this.signaling.close()
    useRoomStore.getState().reset()
  }

  // -------------------------------------------------------------------------
  // Media controls (T077)
  // -------------------------------------------------------------------------

  /**
   * Mute or unmute the local microphone.
   * Pauses/resumes the audio producer (halting RTP transmission) and notifies
   * the server so peers see the updated media state indicator.
   * Safe to call when no audio producer exists (permission-denied peers).
   */
  setMicEnabled(enabled: boolean): void {
    if (this.micProducer) {
      if (enabled) {
        this.micProducer.resume()
      } else {
        this.micProducer.pause()
      }
    }
    this.signaling.sendNotification('setMicEnabled', { enabled })
    useRoomStore.getState().setLocalMediaState({ mic: enabled })
  }

  /**
   * Turn the local camera on or off.
   * Pauses/resumes the video producer (halting RTP transmission) and notifies
   * the server so peers see the updated media state indicator.
   * Safe to call when no video producer exists.
   */
  setCamEnabled(enabled: boolean): void {
    if (this.camProducer) {
      if (enabled) {
        this.camProducer.resume()
      } else {
        this.camProducer.pause()
      }
    }
    this.signaling.sendNotification('setCamEnabled', { enabled })
    useRoomStore.getState().setLocalMediaState({ cam: enabled })
  }

  /**
   * Flip the published camera between front ("user") and back ("environment")
   * facing without renegotiating the mediasoup session.
   *
   * Flow:
   *   1. Guard: ignore re-entrant calls (FR-009) or when no cam producer.
   *   2. Acquire a new video track with the opposite facingMode.
   *   3. `camProducer.replaceTrack` — swaps the outgoing RTP track in place.
   *   4. Stop the old video track; build a new localStream (audio + new video);
   *      update store + this.cameraFacing.
   *   5. On any error: throw so the CALLER surfaces a transient toast. Keep the
   *      old track/producer/facing intact, never null localStream, and stop the
   *      newly-opened camera if the swap didn't complete (no device leak). We do
   *      NOT set localMediaError here — that drives the full-screen permission
   *      panel, which must not appear over a still-working call.
   *   6. Always release the guard.
   */
  async switchCamera(): Promise<void> {
    const camOn = useRoomStore.getState().localMediaState.cam
    // Only switch while the camera is actually on (the control is hidden
    // otherwise); never silently re-open a camera the user turned off.
    if (this.switchInFlight || !this.camProducer || !this.localStream || !camOn) return

    this.switchInFlight = true
    const next = nextFacing(this.cameraFacing)
    // The track we acquire below; cleared once ownership passes to the producer.
    let acquiredTrack: MediaStreamTrack | null = null

    try {
      const mediaResult = await getLocalMedia({
        // Keep the device-appropriate capture target when flipping cameras so the
        // back camera is published at the same profile as the front (005/008).
        video: { ...videoCaptureConstraints(isMobileDevice()), facingMode: { ideal: next } },
        audio: false,
      })

      if (!mediaResult.ok) throw new Error(`switchCamera failed: ${mediaResult.reason}`)

      const newVideoTrack = mediaResult.stream.getVideoTracks()[0]
      if (!newVideoTrack) throw new Error('switchCamera failed: no video track')
      acquiredTrack = newVideoTrack

      // Capture the old video track before replacing.
      const oldVideoTrack = this.localStream.getVideoTracks()[0] ?? null

      // Swap the outgoing RTP track — no renegotiation (FR-003).
      await this.camProducer.replaceTrack({ track: newVideoTrack })
      // Swap succeeded — the producer/stream now own the new track.
      acquiredTrack = null

      // Stop the old track to release the device (FR-005 / SC-005).
      if (oldVideoTrack) {
        oldVideoTrack.stop()
      }

      // Build a new localStream: keep all existing audio tracks + the new video.
      const audioTracks = this.localStream.getAudioTracks()
      const newStream = new MediaStream([...audioTracks, newVideoTrack])
      this.localStream = newStream

      // Update store + internal state (only on success — FR-006 / invariant).
      useRoomStore.getState().setLocalStream(newStream)
      useRoomStore.getState().setCameraFacing(next)
      this.cameraFacing = next
    } catch (err) {
      // Release the camera we just opened if the swap didn't complete, so the
      // device is not left live (FR-005). Old track/producer/facing are
      // unchanged; localStream is never set to null. The caller surfaces a
      // transient toast (FR-006); we deliberately do not set localMediaError.
      acquiredTrack?.stop()
      throw new Error('switchCamera failed', { cause: err })
    } finally {
      this.switchInFlight = false
    }
  }

  /**
   * Count video-input devices via enumerateDevices and update the store.
   * Called after media acquisition and on devicechange to keep the count fresh.
   */
  private async refreshVideoInputCount(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) return
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const count = devices.filter((d) => d.kind === 'videoinput').length
      useRoomStore.getState().setVideoInputCount(count)
    } catch {
      // enumerateDevices can reject in some environments — ignore silently.
    }
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  /**
   * Acquire local media and produce tracks. Sets localMediaError in the store
   * when getUserMedia fails so the UI can surface the permission-denied path.
   */
  private async acquireAndProduce(): Promise<void> {
    const mediaResult = await getLocalMedia({
      video: videoCaptureConstraints(isMobileDevice()),
      audio: true,
    })
    if (mediaResult.ok) {
      await this.produceLocalTracks(mediaResult.stream)
    } else {
      // Joined without media — peer is visible to others but contributes no streams.
      useRoomStore.getState().setLocalMediaState({ mic: false, cam: false })
      useRoomStore.getState().setLocalMediaError(mediaResult.reason)
    }
  }

  /**
   * Store the stream, update the store, and produce audio + video tracks on
   * the send transport. Shared by acquireAndProduce and retryLocalMedia.
   */
  private async produceLocalTracks(stream: MediaStream): Promise<void> {
    this.localStream = stream
    useRoomStore.getState().setLocalStream(stream)
    useRoomStore.getState().setLocalMediaState({ mic: true, cam: true })
    useRoomStore.getState().setLocalMediaError(null)

    const audioTrack = stream.getAudioTracks()[0]
    const videoTrack = stream.getVideoTracks()[0]

    if (audioTrack) await this.produce(audioTrack)
    if (videoTrack) await this.produce(videoTrack)

    // Enumerate devices after media is acquired — permission has been granted
    // at this point so enumerateDevices returns labelled entries (T011).
    void this.refreshVideoInputCount()

    // Subscribe to devicechange so the control hides/shows if cameras are
    // plugged/unplugged during the session.
    if (navigator.mediaDevices && !this.deviceChangeCleanup) {
      const handler = () => { void this.refreshVideoInputCount() }
      navigator.mediaDevices.addEventListener('devicechange', handler)
      this.deviceChangeCleanup = () => {
        navigator.mediaDevices.removeEventListener('devicechange', handler)
      }
    }
  }

  /** Register server→client notification handlers. Called once after join. */
  private subscribeNotifications(): void {
    this.notificationUnsubs.push(
      this.signaling.on('newPeer', (data) => {
        // T062: add the peer with a canonical (empty) stream and default media state.
        // The canonical stream accumulates tracks as newProducer events arrive;
        // the store always holds a fresh clone so VideoTile re-renders on change.
        const canonical = new MediaStream()
        this.peerStreams.set(data.peerId, canonical)

        const peerInfo: PeerInfo = {
          peerId: data.peerId,
          displayName: data.displayName,
          stream: new MediaStream(),
          mediaState: { mic: true, cam: true },
        }
        useRoomStore.getState().addPeer(peerInfo)
        this.peerConsumers.set(data.peerId, new Set())
      }),

      this.signaling.on('newProducer', (data) => {
        // T063: consume the new producer; consumeRemoteProducer handles the
        // canonical-stream accumulation and store clone push.
        void this.handleNewProducer(data.peerId, data.producerId)
      }),

      this.signaling.on('peerLeft', (data) => {
        // T064: close all that peer's consumers, remove from store.
        this.handlePeerLeft(data.peerId)
      }),

      this.signaling.on('producerClosed', (data) => {
        // T065: close just the one consumer, remove track from stream.
        this.handleProducerClosed(data.peerId, data.producerId)
      }),

      this.signaling.on('peerMediaStateChanged', (data) => {
        // T078: patch only the fields present in the notification — do not
        // write undefined over an existing value.
        const patch: { mic?: boolean; cam?: boolean } = {}
        if (data.mic !== undefined) patch.mic = data.mic
        if (data.cam !== undefined) patch.cam = data.cam
        if (Object.keys(patch).length > 0) {
          useRoomStore.getState().patchPeerMediaState(data.peerId, patch)
        }
      }),
    )
  }

  private async handleNewProducer(peerId: string, producerId: string): Promise<void> {
    // Guard: if the canonical stream isn't registered yet, this notification
    // raced ahead of newPeer (should not happen in the normal flow but is
    // safe to drop — the consume loop in join() will pick it up).
    if (!this.peerStreams.has(peerId)) {
      console.warn('RoomClient: newProducer for unknown peer', peerId)
      return
    }

    // Idempotency guard (SHOULD-FIX B): if we already have a consumer for
    // this producer (from the join snapshot or a duplicate notification), do
    // not create a second one — that would orphan the first consumer.
    if (this.producerIndex.has(producerId)) {
      return
    }

    // consumeRemoteProducer adds the track to the canonical stream and pushes
    // a fresh clone to the store — no snapshot/clone of the store stream needed,
    // so concurrent audio+video handlers can't clobber each other.
    await this.consumeRemoteProducer(producerId, peerId)
  }

  private handlePeerLeft(peerId: string): void {
    const consumerIds = this.peerConsumers.get(peerId)
    if (consumerIds) {
      for (const consumerId of consumerIds) {
        const consumer = this.consumers.get(consumerId)
        consumer?.close()
        this.consumers.delete(consumerId)
        this.qualitySamples.delete(consumerId)
      }
      this.peerConsumers.delete(peerId)
    }

    // Remove producerIndex entries for this peer.
    for (const [producerId, entry] of this.producerIndex) {
      if (entry.peerId === peerId) {
        this.producerIndex.delete(producerId)
      }
    }

    // Remove the canonical stream — it's no longer needed.
    this.peerStreams.delete(peerId)

    useRoomStore.getState().removePeer(peerId)
  }

  private handleProducerClosed(peerId: string, producerId: string): void {
    const entry = this.producerIndex.get(producerId)
    if (!entry) return

    const consumer = this.consumers.get(entry.consumerId)
    if (consumer) {
      consumer.close()
      this.consumers.delete(entry.consumerId)
    }
    this.qualitySamples.delete(entry.consumerId)

    // Remove from peerConsumers set.
    this.peerConsumers.get(peerId)?.delete(entry.consumerId)
    this.producerIndex.delete(producerId)

    // Remove the track from the canonical stream and push a fresh clone to the
    // store so VideoTile re-renders with the updated (smaller) track list.
    const canonical = this.peerStreams.get(peerId)
    if (canonical) {
      canonical.removeTrack(entry.track)
      useRoomStore.getState().replacePeerStream(peerId, new MediaStream(canonical.getTracks()))
    }
  }

  // -------------------------------------------------------------------------
  // Connection-quality polling (007)
  // -------------------------------------------------------------------------

  private startQualityPolling(): void {
    if (this.qualityTimer !== null) return
    this.qualityTimer = setInterval(() => {
      void this.pollQuality()
    }, RoomClient.QUALITY_POLL_MS)
  }

  private stopQualityPolling(): void {
    if (this.qualityTimer !== null) {
      clearInterval(this.qualityTimer)
      this.qualityTimer = null
    }
    this.qualitySamples.clear()
  }

  /**
   * One polling tick: classify every remote peer (from its consumers' inbound
   * stats) and the local participant (from the producers' outbound stats), then
   * write the results to the store. Defensive throughout — a getStats() failure
   * or a missing stat field yields 'unknown', never an exception that would kill
   * the interval.
   */
  private async pollQuality(): Promise<void> {
    if (this.leaving) return
    const now = Date.now()
    const store = useRoomStore.getState()

    for (const [peerId, consumerIds] of this.peerConsumers) {
      store.setPeerQuality(peerId, await this.classifyPeer(consumerIds, now))
    }

    store.setLocalQuality(await this.classifyLocal())
  }

  /** Aggregate a remote peer's per-track inbound levels (video-primary, audio fallback). */
  private async classifyPeer(consumerIds: Set<string>, now: number): Promise<QualityLevel> {
    let videoLevel: QualityLevel | null = null
    let audioLevel: QualityLevel | null = null

    for (const consumerId of consumerIds) {
      const consumer = this.consumers.get(consumerId)
      if (!consumer) continue

      const sample = await this.readInboundSample(consumer, now)
      if (!sample) continue

      const prev = this.qualitySamples.get(consumerId)
      const videoActive =
        consumer.kind === 'video' && !consumer.paused && consumer.track.readyState === 'live'
      const { level, emaLossRatio } = classifyInbound(prev, sample, { videoActive })
      sample.emaLossRatio = emaLossRatio
      this.qualitySamples.set(consumerId, sample)

      if (consumer.kind === 'video') videoLevel = level
      else audioLevel = level
    }

    return aggregateLevels(videoLevel, audioLevel)
  }

  /** Classify the local uplink from the producers' remote-inbound (RTCP) stats. */
  private async classifyLocal(): Promise<QualityLevel> {
    const videoLevel = this.camProducer ? await this.readOutboundLevel(this.camProducer) : null
    const audioLevel = this.micProducer ? await this.readOutboundLevel(this.micProducer) : null
    return aggregateLevels(videoLevel, audioLevel)
  }

  /** Build an inbound sample from a consumer's `inbound-rtp` report. */
  private async readInboundSample(
    consumer: Consumer,
    now: number,
  ): Promise<QualitySample | null> {
    let stats: RTCStatsReport
    try {
      stats = await consumer.getStats()
    } catch {
      return null
    }
    for (const value of stats.values()) {
      const r = value as InboundRtpLike
      if (r.type === 'inbound-rtp') {
        return {
          timestampMs: now,
          packetsReceived: typeof r.packetsReceived === 'number' ? r.packetsReceived : undefined,
          packetsLost: typeof r.packetsLost === 'number' ? r.packetsLost : undefined,
          jitterSec: typeof r.jitter === 'number' ? r.jitter : undefined,
        }
      }
    }
    return null
  }

  /** Classify a producer's uplink from its `remote-inbound-rtp` report. */
  private async readOutboundLevel(producer: Producer): Promise<QualityLevel> {
    let stats: RTCStatsReport
    try {
      stats = await producer.getStats()
    } catch {
      return 'unknown'
    }
    for (const value of stats.values()) {
      const r = value as RemoteInboundRtpLike
      if (r.type === 'remote-inbound-rtp') {
        return classifyOutbound({
          timestampMs: 0,
          fractionLost: typeof r.fractionLost === 'number' ? r.fractionLost : undefined,
          roundTripTimeSec: typeof r.roundTripTime === 'number' ? r.roundTripTime : undefined,
        })
      }
    }
    return 'unknown'
  }

  private async createTransport(direction: 'send' | 'recv'): Promise<Transport> {
    if (!this.device) throw new Error('createTransport before device load')

    const params = (await this.signaling.request('createWebRtcTransport', {
      direction,
    } satisfies RequestData<'createWebRtcTransport'>)) as CreateTransportResponseData

    const options: TransportOptions = {
      id: params.transportId,
      iceParameters: params.iceParameters,
      iceCandidates: params.iceCandidates,
      dtlsParameters: params.dtlsParameters,
      ...(params.sctpParameters ? { sctpParameters: params.sctpParameters } : {}),
    }

    const transport =
      direction === 'send'
        ? this.device.createSendTransport(options)
        : this.device.createRecvTransport(options)

    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.signaling
        .request('connectWebRtcTransport', {
          transportId: transport.id,
          dtlsParameters,
        } satisfies RequestData<'connectWebRtcTransport'>)
        .then(() => callback())
        .catch((err: Error) => errback(err))
    })

    if (direction === 'send') {
      transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
        this.signaling
          .request('produce', {
            transportId: transport.id,
            kind,
            rtpParameters,
            appData: appData as Record<string, unknown> | undefined,
          } satisfies RequestData<'produce'>)
          .then((data) => callback({ id: (data as { producerId: string }).producerId }))
          .catch((err: Error) => errback(err))
      })
    }

    return transport
  }

  /**
   * Decide how to publish video, by device class (008, fixed). Mobile forces
   * hardware H.264 (when offered); desktop forces nothing and lets mediasoup-client
   * pick the first ENCODABLE codec in router order (→ VP9) plus K-SVC (006).
   * Crucially we never force a codec the device can't encode — forcing VP9 on a
   * phone would trigger a software VP9 encode. Empty result → library default
   * (join still succeeds, FR-005). Never throws.
   */
  private selectVideoSend(mobile: boolean): {
    codec?: RtpCodecCapability
    scalabilityMode?: string
  } {
    const codecs = this.device?.rtpCapabilities.codecs ?? []
    const choice = chooseVideoSend(codecs, { mobile, scalabilityMode: VIDEO_SCALABILITY_MODE })
    const codec = choice.forceMimeType
      ? codecs.find((c) => c.mimeType.toLowerCase() === choice.forceMimeType!.toLowerCase())
      : undefined
    return {
      ...(codec ? { codec } : {}),
      ...(choice.scalabilityMode ? { scalabilityMode: choice.scalabilityMode } : {}),
    }
  }

  private async produce(track: MediaStreamTrack): Promise<void> {
    if (!this.sendTransport) throw new Error('produce before sendTransport')

    // Video: publish with a device-appropriate codec + profile (008). Desktop
    // keeps VP9 K-SVC (006) at the full bitrate ceiling (005); mobile uses
    // hardware-friendly H.264 (no SVC) at a lighter ceiling so phones — iOS in
    // particular — don't software-encode and glitch. A single encoding (never
    // simulcast) keeps the publisher's upload single-stream (US2). The browser
    // still adapts bitrate down under congestion. Audio uses library defaults.
    let produceOptions: Parameters<typeof this.sendTransport.produce>[0]
    if (track.kind === 'video') {
      const mobile = isMobileDevice()
      const { codec, scalabilityMode } = this.selectVideoSend(mobile)
      const maxBitrate = videoMaxBitrate(mobile)
      debugLog('produce.video', {
        mobile,
        forcedCodec: codec?.mimeType,
        scalabilityMode,
        maxBitrate,
      })
      produceOptions = {
        track,
        encodings: [scalabilityMode ? { maxBitrate, scalabilityMode } : { maxBitrate }],
        codecOptions: { videoGoogleStartBitrate: VIDEO_START_BITRATE },
        ...(codec ? { codec } : {}),
      }
    } else {
      produceOptions = { track }
    }

    const producer = await this.sendTransport.produce(produceOptions)
    this.producers.set(producer.id, producer)
    // Track by kind so setMicEnabled/setCamEnabled can reach the right producer.
    if (producer.kind === 'audio') {
      this.micProducer = producer
    } else if (producer.kind === 'video') {
      this.camProducer = producer
    }
  }

  /**
   * Consume a remote producer and accumulate the resulting track into the
   * canonical per-peer MediaStream (`peerStreams`). After adding the track,
   * pushes a fresh clone to the store via `replacePeerStream` so that
   * VideoTile re-renders with the updated track list.
   *
   * Using the canonical stream as the single accumulator prevents the
   * concurrent-clobber bug: if audio and video newProducer notifications
   * fire in parallel, each handler adds its track to the same canonical stream
   * synchronously (addTrack is synchronous) before the async clone is pushed —
   * the last clone therefore always reflects the full accumulated track set.
   */
  private async consumeRemoteProducer(
    producerId: string,
    peerId: string,
  ): Promise<void> {
    if (!this.recvTransport || !this.device) {
      throw new Error('consumeRemote before recvTransport / device')
    }

    if (!this.peerStreams.has(peerId)) {
      throw new Error(`consumeRemoteProducer: no canonical stream for peer ${peerId}`)
    }

    const data = (await this.signaling.request('consume', {
      producerId,
      rtpCapabilities: this.device.rtpCapabilities,
    } satisfies RequestData<'consume'>)) as ConsumeResponseData

    const consumer = await this.recvTransport.consume({
      id: data.consumerId,
      producerId: data.producerId,
      kind: data.kind,
      rtpParameters: data.rtpParameters as never,
    })

    // ── Liveness check (BLOCKING fix) ────────────────────────────────────────
    // handlePeerLeft may have run during the two awaits above, tearing down the
    // peer and deleting its entries from all maps. Re-fetch liveness signals
    // AFTER the awaits; if the peer is gone (or we are leaving), close the
    // freshly-created consumer so it is never leaked, and return without
    // touching any bookkeeping map.
    if (this.leaving || !this.peerStreams.has(peerId)) {
      consumer.close()
      return
    }

    // Re-fetch the canonical stream post-await — peerStreams may have been
    // replaced if the peer was removed and re-added, though that is not a
    // normal flow. Using the post-await reference is always correct.
    const canonical = this.peerStreams.get(peerId)!

    // The peerConsumers set MUST exist at this point (created alongside
    // peerStreams). If it is absent the peer has been torn down between the
    // liveness check and here — close and bail rather than recreating it.
    const peerSet = this.peerConsumers.get(peerId)
    if (!peerSet) {
      consumer.close()
      return
    }

    this.consumers.set(consumer.id, consumer)

    // Add to the canonical stream (synchronous) — accumulates across concurrent calls.
    canonical.addTrack(consumer.track)

    // Push a fresh clone to the store so zustand notifies React.
    // Because addTrack is synchronous, even if two async handlers call this
    // back-to-back, each clone captures the fully-accumulated track set at
    // the moment it was created — no track is lost.
    useRoomStore.getState().replacePeerStream(peerId, new MediaStream(canonical.getTracks()))

    // Bookkeeping: producerId → { consumerId, peerId, track }
    this.producerIndex.set(producerId, {
      consumerId: consumer.id,
      peerId,
      track: consumer.track,
    })
    // Bookkeeping: peerId → Set<consumerId>
    peerSet.add(consumer.id)

    // Server creates consumers paused; resume so media starts flowing.
    await this.signaling.request('resumeConsumer', {
      consumerId: consumer.id,
    } satisfies RequestData<'resumeConsumer'>)
  }
}

export { SignalingError }
